import { ChecklistFieldDef } from "../components/DynamicChecklistManagement/DynamicChecklistManagement";
import { normalizeDynamicTagId } from "./dynamicChecklistScrutinyPrefill";

export const DOCUMENT_SCHEDULE_TAG_PREFIX = "dynamic-schedule.";

export const DOCUMENT_SCHEDULE_SLOT_TAGS = {
  schedule: `${DOCUMENT_SCHEDULE_TAG_PREFIX}schedule`,
  alloted_to: `${DOCUMENT_SCHEDULE_TAG_PREFIX}alloted_to`,
  survey_no: `${DOCUMENT_SCHEDULE_TAG_PREFIX}survey_no`,
  extent: `${DOCUMENT_SCHEDULE_TAG_PREFIX}extent`,
} as const;

export type DocumentScheduleSlot = keyof typeof DOCUMENT_SCHEDULE_SLOT_TAGS;

export interface DocumentScheduleSet {
  schedule_id?: string;
  schedule?: string;
  alloted_to?: string;
  current_survey?: string;
  survey_no?: string;
  extent?: string;
}

/** Row meta: how many schedule sets to show for a deed entry. */
export const DOCUMENT_SCHEDULE_COUNT_META_KEY = "__document_schedule_count__";

const SCHEDULE_SLOT_ALIASES: Record<string, DocumentScheduleSlot> = {
  schedule: "schedule",
  allotedto: "alloted_to",
  allottedto: "alloted_to",
  alloted_to: "alloted_to",
  surveyno: "survey_no",
  survey_no: "survey_no",
  currentsurvey: "survey_no",
  current_survey: "survey_no",
  extent: "extent",
};

export function isDocumentScheduleMappedField(field: ChecklistFieldDef): boolean {
  if (field.dynamicValueSource === "front_office") return false;
  const tag = normalizeDynamicTagId(field.scrutinyLinkedDeedId);
  return tag.startsWith(DOCUMENT_SCHEDULE_TAG_PREFIX);
}

export function documentScheduleSlotFromTag(
  tag: string | number | undefined | null
): DocumentScheduleSlot | null {
  const norm = normalizeDynamicTagId(tag);
  if (!norm.startsWith(DOCUMENT_SCHEDULE_TAG_PREFIX)) return null;
  const slot = norm.slice(DOCUMENT_SCHEDULE_TAG_PREFIX.length);
  const key = slot.toLowerCase().replace(/[^a-z0-9_]/g, "");
  return SCHEDULE_SLOT_ALIASES[key] ?? null;
}

export function documentScheduleFieldsInGroup(
  fields: ChecklistFieldDef[]
): ChecklistFieldDef[] {
  return fields
    .filter(isDocumentScheduleMappedField)
    .sort((a, b) => a.order - b.order);
}

export function documentScheduleSetValueKey(fieldId: string, setIndex: number): string {
  return `${fieldId}__schedule_set__${setIndex}`;
}

export function getDocumentScheduleSetCount(
  row: Record<string, string>,
  fields: ChecklistFieldDef[]
): number {
  const meta = parseInt(String(row[DOCUMENT_SCHEDULE_COUNT_META_KEY] ?? ""), 10);
  if (Number.isFinite(meta) && meta > 0) return meta;

  let maxIdx = -1;
  fields.forEach((field) => {
    const prefix = `${field.id}__schedule_set__`;
    Object.keys(row).forEach((k) => {
      if (!k.startsWith(prefix)) return;
      const idx = parseInt(k.slice(prefix.length), 10);
      if (Number.isFinite(idx) && idx > maxIdx) maxIdx = idx;
    });
  });
  if (maxIdx >= 0) return maxIdx + 1;
  return 1;
}

function normalizeScheduleSet(raw: Record<string, unknown>): DocumentScheduleSet {
  const scheduleId = String(raw.schedule_id ?? raw.id ?? "").trim();
  const survey =
    String(raw.current_survey ?? raw.survey_no ?? raw.surveyNo ?? "").trim();
  return {
    ...(scheduleId ? { schedule_id: scheduleId } : {}),
    schedule: String(raw.schedule ?? "").trim(),
    alloted_to: String(raw.alloted_to ?? raw.allotted_to ?? "").trim(),
    current_survey: survey,
    survey_no: survey,
    extent: String(raw.extent ?? "").trim(),
  };
}

export function parseDocumentScheduleSetsFromScrutiny(
  row: Record<string, string>
): DocumentScheduleSet[] {
  const jsonRaw = String(row.schedule_deed_json ?? "").trim();
  if (jsonRaw.startsWith("[")) {
    try {
      const parsed = JSON.parse(jsonRaw);
      if (Array.isArray(parsed)) {
        return parsed
          .map((item) =>
            item && typeof item === "object"
              ? normalizeScheduleSet(item as Record<string, unknown>)
              : null
          )
          .filter((item): item is DocumentScheduleSet => !!item)
          .filter(
            (item) =>
              !!(
                item.schedule ||
                item.alloted_to ||
                item.current_survey ||
                item.survey_no ||
                item.extent
              )
          );
      }
    } catch {
      /* fall through */
    }
  }

  const single = normalizeScheduleSet(row);
  if (single.schedule || single.alloted_to || single.current_survey || single.extent) {
    return [single];
  }
  return [];
}

function scheduleSlotValue(set: DocumentScheduleSet, slot: DocumentScheduleSlot): string {
  if (slot === "schedule") return String(set.schedule ?? "").trim();
  if (slot === "alloted_to") return String(set.alloted_to ?? "").trim();
  if (slot === "survey_no") {
    return String(set.current_survey ?? set.survey_no ?? "").trim();
  }
  return String(set.extent ?? "").trim();
}

export function mergeDocumentSchedulePrefill(
  row: Record<string, string>,
  fields: ChecklistFieldDef[],
  documentSets: DocumentScheduleSet[],
  options?: { preferDocument?: boolean }
): void {
  if (!fields.length) return;
  const preferDocument = options?.preferDocument === true;

  const savedCount = preferDocument ? 0 : getDocumentScheduleSetCount(row, fields);
  const savedSets: DocumentScheduleSet[] = [];
  for (let i = 0; i < savedCount; i++) {
    const set: DocumentScheduleSet = {};
    fields.forEach((field) => {
      const slot = documentScheduleSlotFromTag(field.scrutinyLinkedDeedId);
      if (!slot) return;
      const val = String(row[documentScheduleSetValueKey(field.id, i)] ?? "").trim();
      if (slot === "schedule") set.schedule = val;
      else if (slot === "alloted_to") set.alloted_to = val;
      else if (slot === "survey_no") {
        set.current_survey = val;
        set.survey_no = val;
      } else set.extent = val;
    });
    savedSets.push(set);
  }

  const count = Math.max(savedSets.length, documentSets.length, 1);
  row[DOCUMENT_SCHEDULE_COUNT_META_KEY] = String(count);

  for (let i = 0; i < count; i++) {
    const saved = savedSets[i] ?? {};
    const doc = documentSets[i] ?? {};
    fields.forEach((field) => {
      const slot = documentScheduleSlotFromTag(field.scrutinyLinkedDeedId);
      if (!slot) return;
      const savedVal = scheduleSlotValue(saved, slot);
      const docVal = scheduleSlotValue(doc, slot);
      row[documentScheduleSetValueKey(field.id, i)] = savedVal || docVal;
    });
  }

  fields.forEach((field) => {
    row[field.id] = "";
  });
}

export function collectDocumentScheduleSetsFromRow(
  row: Record<string, string>,
  fields: ChecklistFieldDef[]
): DocumentScheduleSet[] {
  if (!fields.length) return [];
  const count = getDocumentScheduleSetCount(row, fields);
  const sets: DocumentScheduleSet[] = [];
  for (let i = 0; i < count; i++) {
    const set: DocumentScheduleSet = {};
    fields.forEach((field) => {
      const slot = documentScheduleSlotFromTag(field.scrutinyLinkedDeedId);
      if (!slot) return;
      const val = String(row[documentScheduleSetValueKey(field.id, i)] ?? "").trim();
      if (!val) return;
      if (slot === "schedule") set.schedule = val;
      else if (slot === "alloted_to") set.alloted_to = val;
      else if (slot === "survey_no") {
        set.current_survey = val;
        set.survey_no = val;
      } else set.extent = val;
    });
    if (set.schedule || set.alloted_to || set.current_survey || set.survey_no || set.extent) {
      sets.push(set);
    }
  }
  return sets;
}

export function documentScheduleRowHasValue(
  row: Record<string, string>,
  fields: ChecklistFieldDef[]
): boolean {
  return collectDocumentScheduleSetsFromRow(row, fields).some(
    (set) =>
      !!(set.schedule || set.alloted_to || set.current_survey || set.survey_no || set.extent)
  );
}

export function documentScheduleFieldRowHasValue(
  row: Record<string, string>,
  field: ChecklistFieldDef,
  allScheduleFields: ChecklistFieldDef[]
): boolean {
  const count = getDocumentScheduleSetCount(row, allScheduleFields);
  for (let i = 0; i < count; i++) {
    const val = String(row[documentScheduleSetValueKey(field.id, i)] ?? "").trim();
    if (val) return true;
  }
  return false;
}
